import { useMemo } from "react";

import { SurveyParticipantJson } from "@performyard/service-contracts";

import {
	useGetParticipantsEligibleForEmail,
	useSurveyScheduleEmailEventsByResourceId,
} from "../../../../../../../api/hooks";

export const useSurveyEmailStatus = (
	surveyId: string,
	disabled: boolean = false
): {
	emailScheduled: boolean;
	scheduledTime: Date | null;
	autoflowId: string | null;
	eligibleParticipants: SurveyParticipantJson[];
	scheduledInTheFuture: boolean;
	refetchSurveyEmailSchedule: () => void;
	refetchEligibleParticipants: () => void;
} => {
	const {
		data: surveyEmailSchedule,
		isLoading: isLoadingSurveyEmailSchedule,
		refetch: refetchSurveyEmailSchedule,
	} = useSurveyScheduleEmailEventsByResourceId(surveyId, {
		enabled: !disabled,
	});

	const {
		data: eligibleParticipantsData,
		isLoading: isLoadingEligibleParticipants,
		refetch: refetchEligibleParticipants,
	} = useGetParticipantsEligibleForEmail(surveyId, {
		enabled: !disabled,
	});

	const { emailScheduled, scheduledTime, autoflowId, eligibleParticipants, scheduledInTheFuture } = useMemo(() => {
		if (disabled) {
			return {
				emailScheduled: false,
				scheduledTime: null,
				autoflowId: null,
				eligibleParticipants: [],
				scheduledInTheFuture: false,
			};
		}

		const emailScheduled =
			!isLoadingSurveyEmailSchedule &&
			surveyEmailSchedule &&
			surveyEmailSchedule.scheduledEvents &&
			surveyEmailSchedule.autoflow?.trigger?.type === "scheduled" &&
			!!surveyEmailSchedule.autoflow?.trigger?.scheduled?.expression?.scheduledTime;
		const scheduledTime =
			surveyEmailSchedule?.autoflow?.trigger?.type === "scheduled" &&
			surveyEmailSchedule.autoflow?.trigger?.scheduled?.expression?.scheduledTime
				? new Date(surveyEmailSchedule.autoflow.trigger.scheduled.expression.scheduledTime)
				: null;
		const scheduledInTheFuture = scheduledTime && scheduledTime > new Date() ? true : false;
		return {
			emailScheduled: Boolean(emailScheduled),
			scheduledTime,
			autoflowId: surveyEmailSchedule?.autoflow?._id ?? null,
			eligibleParticipants: eligibleParticipantsData ?? [],
			scheduledInTheFuture,
		};
	}, [
		disabled,
		isLoadingSurveyEmailSchedule,
		surveyEmailSchedule,
		eligibleParticipantsData,
		isLoadingEligibleParticipants,
	]);

	return {
		emailScheduled,
		scheduledTime,
		autoflowId,
		eligibleParticipants,
		scheduledInTheFuture,
		refetchSurveyEmailSchedule,
		refetchEligibleParticipants,
	};
};
